<template>
  <div
    id="filterContainer"
    class="filter-container"
    :style="'width: calc(100vw - ' + screenWidth + 'px);'"
  >
    <div
      class="add-filter"
      @click="
        () => {
          toggleCategory = !toggleCategory;
          showHiddenFilters = false;
        }
      "
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        class="filter-icon"
      >
        <g clip-path="url(#clip0_267_11223)">
          <path
            d="M10.5001 18C10.3378 18 10.1799 17.9474 10.0501 17.85L7.05011 15.6C6.95696 15.5301 6.88136 15.4396 6.82929 15.3354C6.77722 15.2313 6.75011 15.1164 6.75011 15V10.785L1.48811 4.86525C1.1144 4.44366 0.870392 3.92311 0.785417 3.36618C0.700443 2.80924 0.778116 2.23961 1.0091 1.72576C1.24008 1.21191 1.61455 0.775694 2.08749 0.469549C2.56043 0.163405 3.11173 0.000355814 3.67511 0L14.3251 0C14.8884 0.000660788 15.4396 0.163979 15.9123 0.47033C16.3851 0.776681 16.7593 1.21303 16.9901 1.72694C17.2208 2.24085 17.2982 2.81046 17.213 3.36731C17.1279 3.92416 16.8837 4.44457 16.5099 4.866L11.2501 10.785V17.25C11.2501 17.4489 11.1711 17.6397 11.0304 17.7803C10.8898 17.921 10.699 18 10.5001 18Z"
            fill="#363ABB"
          />
        </g>
        <defs>
          <clipPath id="clip0_267_11223">
            <rect width="18" height="18" fill="white" />
          </clipPath>
        </defs>
      </svg>
      Add filter
    </div>
    <div class="applied-filters-container" ref="appliedFiltersElement">
      <div
        v-for="(item, index) in filterList"
        :key="index"
        :class="`filtered-item ml-2 ${item.hidden ? `hide-item` : ``}`"
      >
        {{ item.category }} : {{ item.label }}
        <img
          class="cursor-pointer"
          :src="filteredItemCloseIcon.src"
          alt="close filtered item"
          @click="removeFilteredItem(item)"
        />
      </div>
    </div>
    <custom-home-hidden-filters
      :show.sync="showHiddenFilters"
      :items="filtersToHide"
      :close-filter-icon-path="filteredItemCloseIcon.src"
      @remove="removeFilteredItem"
    />
    <div v-if="toggleCategory" class="category-container">
      <div
        class="category-item"
        v-for="(category, index) in localCategories"
        :key="index"
        :id="category.label"
        @mouseover="handleMouseOver"
        @mouseleave="handleMouseLeave"
      >
        <span style="margin-left: 14px;"> {{ category.label }} </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="7"
          height="9"
          viewBox="0 0 7 9"
          fill="none"
          style="margin-right: 13.8px;"
        >
          <path
            d="M5.26172 4.29999L1.00515 0.999992"
            stroke="#363ABB"
            stroke-width="1.5"
            stroke-linecap="round"
          />
          <path
            d="M5.15576 4.54199L0.999727 8.19992"
            stroke="#363ABB"
            stroke-width="1.5"
            stroke-linecap="round"
          />
        </svg>
        <div
          v-show="category.show && category.label === 'Patient'"
          class="filter-selector p-2"
        >
          <custom-multiple-selector
            :options="category.options"
            v-model="appliedFilters"
            theme="Patient"
            :stringQuery.sync="eocsFilters.searchPhrase"
            :loading="loading"
            :hideAllOption="true"
          />
        </div>
        <div
          v-show="category.show && category.label === 'Date Range'"
          class="filter-date-range-selector p-2"
        >
          <date-range
            :start-date.sync="startDate"
            :end-date.sync="endDate"
            @applyFilter="
              () => {
                category.show = false;
                applyDateRangeFilter();
              }
            "
            @cancelFilter="
              () => {
                clearDateRangeAppliedFilters();
                toggleCategory = false;
              }
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import HomeDropdownSelector from "./HomeDropdownSelector.vue";
import CustomMultipleSelector from "./CustomMultipleSelector.vue";
import CustomHomeHiddenFilters from "./CustomHomeHiddenFilters.vue";
import DateRange from "./DateRange.vue";
import eocServer from "../../server/eoc";
import { getSidebarCollapsed } from "../../helpers/session";
import { mapState } from "vuex";

export default defineComponent({
  name: "CustomHomeFilters",
  components: { CustomMultipleSelector, DateRange, CustomHomeHiddenFilters },
  props: {
    categories: {
      type: Array,
      default: () => []
    },
    value: [],
    showing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      toggleCategory: false,
      localCategories: this.categories.map(cat => ({
        label: cat.label,
        show: false,
        options: []
      })),
      appliedFilters: [],
      filteredItemCloseIcon: {
        src: require("../../assets/icons/filter-close-icon.png")
      },
      filtersWidthObserver: null,
      filtersToHide: [],
      showHiddenFilters: false,
      startDate: null,
      endDate: null,
      eocsFilters: {
        page: 0,
        pageSize: 40,
        searchPhrase: ""
      },
      eocs: [],
      loading: false,
      sideBarCollapsed: getSidebarCollapsed()
    };
  },
  async mounted() {
    this.$nextTick(() => {
      const extraFiltersComponent = this.$refs.appliedFiltersElement;
      this.filtersWidthObserver = new ResizeObserver(this.handleFiltersChanged);
      this.filtersWidthObserver.observe(extraFiltersComponent);
    });
    document.addEventListener("click", this.clickOutside);
    await this.fetchEocs();
  },
  beforeUnmount() {
    document.removeEventListener("click", this.clickOutside);
  },
  computed: {
    ...mapState("layout", {
      sidebarChange: state => state.sidebarChange
    }),
    screenWidth() {
      return this.sideBarCollapsed ? "1075" : "1255";
    },
    filterList() {
      return this.appliedFilters.map(item => {
        const isHiden =
          this.filtersToHide.filter(
            hiddenFilter => hiddenFilter.value === item.value
          ).length > 0;
        return {
          ...item,
          hidden: isHiden
        };
      });
    }
  },
  methods: {
    getComponent(label) {
      switch (label) {
        case "Patient":
          return HomeDropdownSelector;
        default:
          break;
      }
    },
    clickOutside(event) {
      if (!(this.$el == event.target || this.$el.contains(event.target))) {
        this.toggleCategory = false;
      }
    },
    handleMouseOver(event) {
      const parentElement = event.target.parentNode;
      const category = event.target.id;

      if (category && parentElement.classList.contains("category-container")) {
        const item = this.localCategories.find(cat => cat.label === category);
        item.show = true;
      }
    },
    handleMouseLeave(event) {
      const category = event.target.id;
      if (category) {
        const item = this.localCategories.find(cat => cat.label === category);
        item.show = false;
      }
    },
    removeFilteredItem(item) {
      const appliedIndex = this.appliedFilters.findIndex(
        filter => filter.value === item.value
      );

      if (appliedIndex !== -1) {
        this.appliedFilters.splice(appliedIndex, 1);
      }

      if (item.hidden) {
        const hideIndex = this.filtersToHide.findIndex(
          filter => filter.value === item.value
        );
        if (hideIndex !== -1) {
          this.filtersToHide.splice(hideIndex, 1);
        }
        this.showHiddenFilters = true;
      }

      if (item.category === "Date Range") {
        this.startDate = "";
        this.endDate = "";
      }
    },
    clearDateRangeAppliedFilters() {
      const dateRangeFilterIndex = this.appliedFilters.findIndex(
        filter => filter.category === "Date Range"
      );

      if (dateRangeFilterIndex !== -1) {
        this.appliedFilters.splice(dateRangeFilterIndex, 1);
      }

      const dateRangeHiddenFilterIndex = this.filtersToHide.findIndex(
        filter => filter.category === "Date Range"
      );

      if (dateRangeHiddenFilterIndex !== -1) {
        this.filtersToHide.splice(dateRangeHiddenFilterIndex, 1);
      }
    },
    handleFiltersChanged() {
      this.filtersToHide = [];
      this.$nextTick(() => {
        if (!this.showing) {
          return;
        }
        let toHide = [];
        let compareWidth = document.getElementById("filterContainer")
          .clientWidth;
        let filters = [...(this.$refs.appliedFiltersElement?.children || [])];
        let widthSum = 0;
        let rightMargin = 100;
        for (let i = 0; i < filters.length; i++) {
          if (widthSum + filters[i].clientWidth + rightMargin > compareWidth) {
            toHide.push(this.appliedFilters[i]);
          } else {
            widthSum += filters[i].clientWidth + rightMargin;
          }
        }
        this.$nextTick(() => (this.filtersToHide = toHide));
      });
    },
    applyDateRangeFilter() {
      const dateRangeItemIndex = this.appliedFilters.findIndex(
        item => item.category === "Date Range"
      );

      if (dateRangeItemIndex != -1) {
        this.appliedFilters.splice(dateRangeItemIndex, 1);
      }

      this.appliedFilters.push({
        category: "Date Range",
        label: `From: ${this.formatDateOnly(
          this.startDate
        )} To: ${this.formatDateOnly(this.endDate)}`,
        startDate: this.startDate ?? null,
        endDate: this.endDate ?? null
      });
      this.toggleCategory = false;
    },
    async fetchEocs(inmediate) {
      if (this.lastTimeout) {
        clearTimeout(this.lastTimeout);
        this.lastTimeout = null;
      }
      this.lastTimeout = setTimeout(
        async () => {
          this.loading = true;
          const optionsResult = await eocServer.queryList(
            {
              eocStatus: ["Active", "Pending"],
              deleted: 0,
              allLocations: true,
              page: this.eocsFilters.page,
              pageSize: this.eocsFilters.pageSize,
              searchPhrase: this.eocsFilters.searchPhrase
            },
            {
              "Hide-Loading": true
            }
          );
          const data = optionsResult.episodesOfCare.map(eoc => ({
            value: eoc.id,
            label: this.formatEocObj(eoc),
            data: eoc,
            category: "Patient"
          }));
          this.eocs = data;
          const patientCategory = this.localCategories.find(
            item => item.label === "Patient"
          );
          patientCategory.options = this.eocs;
          this.loading = false;
        },
        inmediate ? 0 : 2000
      );
    }
  },
  watch: {
    appliedFilters: {
      handler: function(value) {
        if (value) {
          this.$emit("input", [...value]);
        }
      },
      deep: true
    },
    "eocsFilters.searchPhrase": {
      handler: async function(value) {
        this.eocsFilters.searchPhrase = value;
        await this.fetchEocs();
        const patientCategory = this.localCategories.find(
          item => item.label === "Patient"
        );
        patientCategory.show = true;
      },
      deep: true
    },
    showHiddenFilters(value) {
      if (value) this.toggleCategory = false;
    },
    sidebarChange: function(newValue) {
      if (newValue) {
        setTimeout(() => {
          this.sideBarCollapsed = getSidebarCollapsed();
        }, 500);
      }
    },
    filterList: {
      handler: function(value) {
        const uniqueInFiltersToHide = this.filtersToHide.filter(
          filterToHide => {
            return !value.some(
              appliedFilter => filterToHide.value === appliedFilter.value
            );
          }
        );

        if (uniqueInFiltersToHide.length > 0) {
          for (let index = 0; index < uniqueInFiltersToHide.length; index++) {
            const unique = uniqueInFiltersToHide[index];

            const uIndex = this.filtersToHide.findIndex(
              item => item.value === unique.value
            );

            if (uIndex !== -1) {
              this.filtersToHide.splice(uIndex, 1);
            }
          }
        }
      }
    }
  }
});
</script>

<style scoped>
.filter-container {
  position: relative;
  display: flex;
  align-items: center;
  height: 45px;
  flex-shrink: 0;
  border-radius: 7px;
  background: var(--white, #fff);
  box-shadow: 0px 0px 3px 0px rgba(0, 0, 0, 0.3);
  color: #646a7a;
  font-family: Bahnschrift;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.category-container {
  position: absolute;
  z-index: 1000;
  width: 201px;
  flex-shrink: 0;
  border-radius: 7px;
  border: 1px solid var(--gray-200, #e5e7eb);
  background: var(--white, #fff);
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.14);
  top: 48px;
  display: flex;
  flex-direction: column;
  margin-top: 2px;

  /* :hover {
    background: rgba(54, 58, 187, 0.04);
  } */
}

.category-item {
  border-radius: 6px;
  width: 100%;
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  position: relative;

  /* :not(.category-container) {
    background-color: #e5e7eb;
  } */
}

.filter-selector {
  position: absolute;
  z-index: 1;
  width: 276px;
  flex-shrink: 0;
  border-radius: 7px;
  border: 1px solid var(--gray-200, #e5e7eb);
  background: var(--white, #fff);
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.14);
  left: 199px;
  top: 0;
  height: auto;
  padding-top: var(--25, 10px);
}

.filter-date-range-selector {
  position: absolute;
  z-index: 1;
  width: auto;
  flex-shrink: 0;
  border-radius: 7px;
  border: 1px solid var(--gray-200, #e5e7eb);
  background: var(--white, #fff);
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.14);
  left: 199px;
  top: 0;
  height: auto;
  padding-top: var(--25, 10px);
}

.add-filter {
  cursor: pointer;
  border-bottom: 2px solid #363abb;
  margin: 20px 0 14px 12px;
  height: 25px;
}

.filtered-item {
  border-radius: 5px;
  background: rgba(205, 210, 229, 0.5);
  display: inline-flex;
  padding: 6px 9px;
  align-items: center;
  gap: 16px;
  height: auto;
}

.hide-item {
  display: none;
}

@media (max-width: 1500px) {
  .filter-container {
    width: 100% !important;
  }
}
</style>
